/* ============================================================================
   Biotique PDP v2 — interactions (vanilla JS, no build step).
   Defensive: every initialiser no-ops when its nodes are absent, so the file
   is safe to load site-wide. The alternate template owns its sticky-bar
   visibility so the existing product templates keep their current behavior.
   ========================================================================== */
(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState !== "loading") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn);
    }
  }

  /* --- Gallery: scroll-snap carousel + desktop thumbnail rail ------------ */
  function initGallery(root) {
    var track = root.querySelector("[data-gallery-track]");
    var dotsWrap = root.querySelector(".pdp-v2__dots");
    var thumbsWrap = root.querySelector("[data-gallery-thumbs]");
    var thumbButtons = Array.prototype.slice.call(
      root.querySelectorAll("[data-gallery-thumb]")
    );
    var galleryPrev = root.querySelector('[data-gallery-arrow="prev"]');
    var galleryNext = root.querySelector('[data-gallery-arrow="next"]');
    var thumbPrev = root.querySelector('[data-gallery-thumb-scroll="prev"]');
    var thumbNext = root.querySelector('[data-gallery-thumb-scroll="next"]');
    if (!track) return;

    var slides = Array.prototype.slice.call(
      track.querySelectorAll(".pdp-v2__gallery-slide")
    );
    if (!slides.length) return;

    var currentIndex = 0;
    var dots = [];

    function updateGalleryArrows() {
      if (galleryPrev) galleryPrev.disabled = currentIndex <= 0;
      if (galleryNext) galleryNext.disabled = currentIndex >= slides.length - 1;
    }

    function updateThumbScrollControls() {
      if (!thumbsWrap || !thumbPrev || !thumbNext) return;
      var horizontal = thumbsWrap.scrollWidth > thumbsWrap.clientWidth + 2;
      var maxScroll = Math.max(
        0,
        horizontal
          ? thumbsWrap.scrollWidth - thumbsWrap.clientWidth
          : thumbsWrap.scrollHeight - thumbsWrap.clientHeight
      );
      var position = horizontal ? thumbsWrap.scrollLeft : thumbsWrap.scrollTop;
      var isScrollable = maxScroll > 2;
      thumbPrev.hidden = !isScrollable || position <= 2;
      thumbNext.hidden = !isScrollable || position >= maxScroll - 2;
      thumbPrev.disabled = !isScrollable || position <= 2;
      thumbNext.disabled = !isScrollable || position >= maxScroll - 2;
    }

    function revealActiveThumb(thumb) {
      if (!thumbsWrap || !thumb) return;
      var horizontal = thumbsWrap.scrollWidth > thumbsWrap.clientWidth + 2;

      if (horizontal) {
        var left = thumb.offsetLeft;
        var right = left + thumb.offsetWidth;
        var viewLeft = thumbsWrap.scrollLeft;
        var viewRight = viewLeft + thumbsWrap.clientWidth;

        if (left < viewLeft) {
          thumbsWrap.scrollTo({ left: Math.max(0, left - 3), behavior: "smooth" });
        } else if (right > viewRight) {
          thumbsWrap.scrollTo({
            left: right - thumbsWrap.clientWidth + 3,
            behavior: "smooth"
          });
        }
        return;
      }

      var top = thumb.offsetTop;
      var bottom = top + thumb.offsetHeight;
      var viewTop = thumbsWrap.scrollTop;
      var viewBottom = viewTop + thumbsWrap.clientHeight;

      if (top < viewTop) {
        thumbsWrap.scrollTo({ top: Math.max(0, top - 3), behavior: "smooth" });
      } else if (bottom > viewBottom) {
        thumbsWrap.scrollTo({
          top: bottom - thumbsWrap.clientHeight + 3,
          behavior: "smooth"
        });
      }
    }

    // Leaving a slide must stop its video, otherwise audio keeps playing over
    // the next image. Native <video> pauses directly; third-party iframes are
    // cross-origin, so the postMessage contract is the only lever available.
    function pauseSlideMedia(slide) {
      if (!slide) return;

      var video = slide.querySelector("video");
      if (video && typeof video.pause === "function" && !video.paused) {
        video.pause();
      }

      var iframe = slide.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        var src = iframe.src || "";
        try {
          if (src.indexOf("youtube") !== -1) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              "*"
            );
          } else if (src.indexOf("vimeo") !== -1) {
            iframe.contentWindow.postMessage('{"method":"pause"}', "*");
          }
        } catch (e) {
          /* cross-origin frame not ready — nothing further we can do */
        }
      }
    }

    // Autoplay is scoped to the visible slide. The markup carries the autoplay
    // attribute so a video-first gallery starts unaided, but every off-screen
    // video is paused on init and only resumed once its slide is active.
    function playSlideMedia(slide) {
      if (!slide) return;

      var video = slide.querySelector("video");
      if (!video || typeof video.play !== "function" || !video.paused) return;

      // Muted is what makes autoplay permissible; never resume with sound
      // unless the shopper themselves unmuted this video.
      if (!video.dataset.userUnmuted) {
        video.muted = true;
      }

      var attempt = video.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(function () {
          /* blocked by the browser — controls remain for manual playback */
        });
      }
    }

    function setActive(idx) {
      idx = Math.max(0, Math.min(idx, slides.length - 1));
      if (idx !== currentIndex) {
        pauseSlideMedia(slides[currentIndex]);
      }
      currentIndex = idx;
      playSlideMedia(slides[idx]);

      dots.forEach(function (dot, i) {
        dot.classList.toggle("is-active", i === idx);
        dot.setAttribute("aria-pressed", i === idx ? "true" : "false");
      });

      thumbButtons.forEach(function (thumb, i) {
        var active = i === idx;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-pressed", active ? "true" : "false");
        if (active) {
          revealActiveThumb(thumb);
        }
      });

      updateGalleryArrows();
      updateThumbScrollControls();
    }

    function goTo(idx, behavior) {
      idx = Math.max(0, Math.min(idx, slides.length - 1));
      var slide = slides[idx];
      if (!slide) return;
      setActive(idx);
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: behavior || "smooth"
      });
    }

    // The autoplay attribute would otherwise start every video in the gallery
    // at once, including the ones off-screen. Hold them until their slide shows.
    slides.forEach(function (slide, i) {
      var video = slide.querySelector("video");
      if (!video) return;

      if (i !== 0) {
        video.autoplay = false;
        video.pause();
      }

      // Respect a shopper who turns sound on: never silently re-mute them.
      video.addEventListener("volumechange", function () {
        if (video.muted) {
          delete video.dataset.userUnmuted;
        } else {
          video.dataset.userUnmuted = "true";
        }
      });
    });

    if (dotsWrap) {
      dotsWrap.innerHTML = "";
      dots = slides.map(function (slide, i) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.className = "pdp-v2__dot" + (i === 0 ? " is-active" : "");
        dot.setAttribute("aria-label", "Go to image " + (i + 1));
        dot.setAttribute("aria-pressed", i === 0 ? "true" : "false");
        dot.addEventListener("click", function () {
          goTo(i);
        });
        dotsWrap.appendChild(dot);
        return dot;
      });
    }

    if (slides.length <= 1) {
      if (dotsWrap) dotsWrap.style.display = "none";
      setActive(0);
      root.__pdpGalleryGoTo = function () {};
      return;
    }

    thumbButtons.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () {
        goTo(i);
      });
    });

    if (galleryPrev) {
      galleryPrev.addEventListener("click", function () {
        goTo(currentIndex - 1);
      });
    }
    if (galleryNext) {
      galleryNext.addEventListener("click", function () {
        goTo(currentIndex + 1);
      });
    }

    if (thumbsWrap && thumbPrev && thumbNext) {
      thumbPrev.addEventListener("click", function () {
        var horizontal = thumbsWrap.scrollWidth > thumbsWrap.clientWidth + 2;
        var distance = horizontal
          ? Math.max(80, thumbsWrap.clientWidth * 0.7)
          : Math.max(80, thumbsWrap.clientHeight * 0.7);
        thumbsWrap.scrollBy(
          horizontal
            ? { left: -distance, behavior: "smooth" }
            : { top: -distance, behavior: "smooth" }
        );
      });
      thumbNext.addEventListener("click", function () {
        var horizontal = thumbsWrap.scrollWidth > thumbsWrap.clientWidth + 2;
        var distance = horizontal
          ? Math.max(80, thumbsWrap.clientWidth * 0.7)
          : Math.max(80, thumbsWrap.clientHeight * 0.7);
        thumbsWrap.scrollBy(
          horizontal
            ? { left: distance, behavior: "smooth" }
            : { top: distance, behavior: "smooth" }
        );
      });
      thumbsWrap.addEventListener("scroll", updateThumbScrollControls, { passive: true });
    }

    var raf;
    track.addEventListener("scroll", function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var center = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        slides.forEach(function (slide, i) {
          var slideCenter = slide.offsetLeft + slide.clientWidth / 2 - track.offsetLeft;
          var dist = Math.abs(slideCenter - center);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        setActive(best);
      });
    });

    // expose for variant→media jump
    root.__pdpGalleryGoTo = function (idx) {
      goTo(idx);
    };

    setActive(0);
    window.setTimeout(updateThumbScrollControls, 0);
    window.addEventListener("load", updateThumbScrollControls, { once: true });
    window.addEventListener("resize", updateThumbScrollControls, { passive: true });
  }

  /* --- Native size cards: horizontal scroller with prev/next buttons ----- */
  var SIZE_SCROLL_START_TOLERANCE = 4;

  function revealTrackItem(track, item, behavior) {
    if (!track || !item) return;

    // offsetLeft is layout-based, so it stays correct even when the track has
    // not settled yet (scroll-snap can still be re-anchoring during init).
    // getBoundingClientRect deltas were unreliable at that moment and left an
    // off-screen selected card unscrolled on first load.
    var itemLeft = item.offsetLeft - track.offsetLeft;
    var itemRight = itemLeft + item.offsetWidth;
    var viewLeft = track.scrollLeft;
    var viewRight = viewLeft + track.clientWidth;
    var edgeGap = 4;
    var target = viewLeft;

    if (itemLeft < viewLeft + edgeGap) {
      target = itemLeft - edgeGap;
    } else if (itemRight > viewRight - edgeGap) {
      target = itemRight - track.clientWidth + edgeGap;
    }

    var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    target = Math.max(0, Math.min(target, maxScroll));
    if (Math.abs(target - track.scrollLeft) <= 1) return;

    // scroll-snap-type on these tracks can undo a smooth programmatic scroll
    // that starts before layout settles, so the initial reveal is set directly.
    if (behavior === "auto") {
      track.scrollLeft = target;
    } else {
      track.scrollTo({ left: target, behavior: behavior || "smooth" });
    }
  }

  function initSizeScroller(root) {
    var scroller = root.querySelector(".pdp-v2__sizes-scroller");
    if (!scroller) return;
    var track = scroller.querySelector(".pdp-v2__sizes");
    var prev = scroller.querySelector(".pdp-v2__sizes-nav--prev");
    var next = scroller.querySelector(".pdp-v2__sizes-nav--next");
    if (!track || !prev || !next) return;

    function update() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 2) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = track.scrollLeft <= SIZE_SCROLL_START_TOLERANCE;
      next.hidden = track.scrollLeft >= maxScroll - 1;
    }
    function step(direction) {
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: "smooth" });
    }
    function revealActive(behavior) {
      var active = track.querySelector("input:checked");
      var card = active && active.closest(".pdp-v2__size");
      revealTrackItem(track, card, behavior);
    }
    function refresh() {
      revealActive("auto");
      update();
    }

    prev.addEventListener("click", function () { step(-1); });
    next.addEventListener("click", function () { step(1); });
    track.addEventListener("scroll", update);
    track.addEventListener("change", function (event) {
      if (!event.target.matches('input[type="radio"]')) return;
      window.requestAnimationFrame(function () {
        revealActive("smooth");
        update();
      });
    });
    window.addEventListener("resize", refresh);
    // Recheck overflow after late layout (fonts/images) so the buttons reliably
    // appear whenever the track is actually scrollable, not only at first paint.
    if (typeof ResizeObserver !== "undefined") {
      try { new ResizeObserver(refresh).observe(track); } catch (e) {}
    }
    window.addEventListener("load", refresh);
    window.addEventListener("pageshow", refresh);
    revealActive("auto");
    update();
    window.requestAnimationFrame(refresh);
    window.setTimeout(refresh, 150);
  }

  /* --- Sibling size cards: refresh navigation after late layout --------- */
  function initSiblingScrollerRefresh(root) {
    var scrollers = root.querySelectorAll(
      ".m-sibling-selector--pdp .m-sibling-selector__scroller"
    );

    Array.prototype.forEach.call(scrollers, function (scroller) {
      var track = scroller.querySelector(".m-sibling-selector__track");
      var prev = scroller.querySelector(".m-sibling-selector__nav--prev");
      var next = scroller.querySelector(".m-sibling-selector__nav--next");
      if (!track || !prev || !next) return;

      function update() {
        var maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll <= 2) {
          prev.hidden = true;
          next.hidden = true;
          return;
        }
        prev.hidden = track.scrollLeft <= SIZE_SCROLL_START_TOLERANCE;
        next.hidden = track.scrollLeft >= maxScroll - 1;
      }
      function revealActive() {
        var active = track.querySelector(
          '.m-sibling-pill.is-active, .m-sibling-pill[aria-current="true"]'
        );
        revealTrackItem(track, active, "auto");
      }
      function refresh() {
        revealActive();
        update();
      }

      if (typeof ResizeObserver !== "undefined") {
        try { new ResizeObserver(refresh).observe(track); } catch (e) {}
      }
      window.addEventListener("resize", refresh);
      window.addEventListener("load", refresh);
      window.addEventListener("pageshow", refresh);
      revealActive();
      update();
      window.requestAnimationFrame(refresh);
      window.setTimeout(refresh, 150);
    });
  }

  /* --- Native variant size cards → price / ATC / sticky sync ------------- */
  function money(node) {
    return node ? node.getAttribute("data-price") : "";
  }

  function initSizeCards(root) {
    var inputs = Array.prototype.slice.call(
      root.querySelectorAll(".pdp-v2__size input[type=radio]")
    );
    if (!inputs.length) return;

    function apply(input) {
      if (!input) return;
      var price = input.getAttribute("data-price") || "";
      var compare = input.getAttribute("data-compare") || "";
      var off = input.getAttribute("data-off") || "";
      var priceCents = parseInt(input.getAttribute("data-price-cents"), 10) || 0;
      var compareCents = parseInt(input.getAttribute("data-compare-cents"), 10) || priceCents;
      var available = input.getAttribute("data-available") !== "false";
      var mediaIdx = input.getAttribute("data-media-index");
      var variantLabel = input.getAttribute("data-variant-label") || "";
      if (variantLabel.trim().toLowerCase() === "default title") variantLabel = "";

      // Price block
      setText(root, ".pdp-v2__price-now", price);
      toggleTextAll(root, ".pdp-v2__price-compare, .pdp-v2__sticky-compare", compare);
      var saveEl = root.querySelector(".pdp-v2__price-save");
      if (saveEl) {
        if (!off && compareCents > priceCents) {
          off = Math.floor(((compareCents - priceCents) * 100) / compareCents);
        }
        if (off) {
          saveEl.style.display = "";
          saveEl.textContent = off + "% OFF";
        } else {
          saveEl.style.display = "none";
        }
      }

      // Mirror into every .m-variant-price (sticky bar + anywhere else)
      root.querySelectorAll(".m-variant-price").forEach(function (n) {
        n.innerHTML = price;
      });

      // ATC inline price label
      setText(root, ".pdp-v2__atc-price", price);
      var stickyVariant = root.querySelector(".m-sticky-variant-label");
      var productSize = stickyVariant && stickyVariant.getAttribute("data-product-size");
      if (stickyVariant && (productSize || variantLabel)) {
        stickyVariant.textContent = productSize || variantLabel;
      }

      // Hidden id input (so non-radio sticky form stays in sync if present)
      root.querySelectorAll(".m-variant-input, .m_product_varaint_input").forEach(function (n) {
        var previous = n.value;
        if (input.value) n.value = input.value;
        ["price", "priceCents", "compare", "compareCents", "available"].forEach(function (key) {
          var attr = "data-" + key.replace(/[A-Z]/g, function (m) { return "-" + m.toLowerCase(); });
          var value = input.getAttribute(attr);
          if (value != null) n.setAttribute(attr, value);
        });
        if (previous !== n.value) {
          n.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });

      // Availability → all ATC buttons
      root.querySelectorAll(".pdp-v2__atc").forEach(function (btn) {
        btn.disabled = !available;
        var label = btn.querySelector(".pdp-v2__atc-label");
        if (label) {
          label.textContent = available
            ? (btn.classList.contains("pdp-v2__sticky-btn") ? "Add to cart" : "Add to bag")
            : "Out of stock";
        }
        var separator = btn.querySelector(".pdp-v2__atc-sep");
        var stickyPrice = btn.querySelector(".pdp-v2__sticky-button-price");
        if (separator && btn.classList.contains("pdp-v2__sticky-btn")) separator.hidden = !available;
        if (stickyPrice) stickyPrice.hidden = !available;
      });

      // Jump gallery to the variant's media, if mapped
      if (mediaIdx != null && root.__pdpGalleryGoTo) {
        root.__pdpGalleryGoTo(parseInt(mediaIdx, 10) || 0);
      }
    }

    inputs.forEach(function (input) {
      input.addEventListener("change", function () {
        if (input.checked) apply(input);
      });
    });

    var checked = inputs.filter(function (i) { return i.checked; })[0] || inputs[0];
    apply(checked);
  }

  function setText(root, sel, val) {
    var el = root.querySelector(sel);
    if (el) el.innerHTML = val;
  }
  function toggleText(root, sel, val) {
    var el = root.querySelector(sel);
    if (!el) return;
    if (val) {
      el.style.display = "";
      el.innerHTML = val;
    } else {
      el.style.display = "none";
    }
  }
  function toggleTextAll(root, sel, val) {
    root.querySelectorAll(sel).forEach(function (el) {
      if (val) {
        el.style.display = "";
        el.innerHTML = val;
      } else {
        el.style.display = "none";
        el.innerHTML = "";
      }
    });
  }

  /* --- Quantity steppers ------------------------------------------------- */
  function initQty(root) {
    root.querySelectorAll(".pdp-v2__qty").forEach(function (box) {
      var input = box.querySelector("input");
      if (!input) return;
      box.querySelectorAll("button").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var step = btn.classList.contains("pdp-v2__qty-add") ? 1 : -1;
          var next = (parseInt(input.value, 10) || 1) + step;
          input.value = next < 1 ? 1 : next;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    });
  }

  /* --- Read more / read less -------------------------------------------- */
  function initReadMore(root) {
    root.querySelectorAll(".pdp-v2__readmore").forEach(function (btn) {
      if (btn.hasAttribute("data-pdp-aplus-toggle")) return;
      var target = root.querySelector(btn.getAttribute("data-target"));
      if (!target) return;
      // Only show the toggle if the content actually overflows.
      if (target.scrollHeight <= target.clientHeight + 4) {
        target.classList.remove("is-clamped");
        btn.style.display = "none";
        return;
      }
      btn.addEventListener("click", function () {
        var clamped = target.classList.toggle("is-clamped");
        btn.textContent = clamped ? "Read more" : "Read less";
        btn.classList.toggle("is-open", !clamped);
        if (!clamped) target.classList.add("reveal");
      });
    });
  }

  function initAPlusContent(scope) {
    scope.querySelectorAll("[data-pdp-aplus-toggle]").forEach(function (btn) {
      if (btn.__pdpAplusBound) return;
      btn.__pdpAplusBound = true;

      var target = scope.querySelector(btn.getAttribute("data-target")) || document.querySelector(btn.getAttribute("data-target"));
      if (!target) return;

      btn.addEventListener("click", function () {
        var expanded = !target.classList.contains("is-expanded");
        target.classList.toggle("is-expanded", expanded);
        target.classList.toggle("is-clamped", !expanded);
        btn.textContent = expanded ? "Read less" : "Read more";
        btn.classList.toggle("is-open", expanded);
      });
    });
  }

  /* --- Coupon / offer copy ---------------------------------------------- */
  function initCouponCopy(root) {
    root.querySelectorAll(".pdp-v2__offer-copy").forEach(function (btn) {
      btn.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        var code = btn.getAttribute("data-code") || "";
        if (!code) return;
        var done = function () {
          var isIconButton = btn.classList.contains("pdp-v2__offers-dd-copy");
          var original = btn.textContent;
          var originalLabel = btn.getAttribute("aria-label");
          btn.classList.add("is-copied");
          if (isIconButton) {
            btn.setAttribute("aria-label", "Copied code " + code);
          } else {
            btn.textContent = "✓ Copied";
          }
          setTimeout(function () {
            btn.classList.remove("is-copied");
            if (isIconButton) {
              if (originalLabel) btn.setAttribute("aria-label", originalLabel);
            } else {
              btn.textContent = original;
            }
          }, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done).catch(done);
        } else {
          var ta = document.createElement("textarea");
          ta.value = code;
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand("copy"); } catch (e) {}
          document.body.removeChild(ta);
          done();
        }
      });
    });
  }

  /* --- Zipcode checker app polish --------------------------------------- */
  function initPincodeState(root) {
    var boxes = root.querySelectorAll(".pdp-v2__pincode");
    if (!boxes.length) return;

    boxes.forEach(function (box) {
      if (box.__pdpPincodeBound) return;
      box.__pdpPincodeBound = true;

      function update() {
        var text = (box.textContent || "").toLowerCase();
        var hasNegative = /sorry|not available|not deliver|do not deliver|unavailable|not serviceable/.test(text);
        var hasPositive = /delivery is available|service available|available in your area|deliver to your location/.test(text) && !hasNegative;

        box.classList.toggle("is-serviceable", hasPositive);
        box.classList.toggle("is-unserviceable", hasNegative);
      }

      var queued = false;
      function queueUpdate() {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          update();
        });
      }

      if (typeof MutationObserver !== "undefined") {
        var observer = new MutationObserver(queueUpdate);
        observer.observe(box, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ["class", "style"]
        });
      }

      box.addEventListener("input", queueUpdate, true);
      box.addEventListener("click", function () {
        window.setTimeout(queueUpdate, 250);
      }, true);
      queueUpdate();
    });
  }

  /* --- AJAX add-to-cart + open drawer (non-sibling PDPs) ----------------
     Sibling PDPs are already intercepted in m-custom.js (it preventDefaults
     and ajax-adds only when `.m-sibling-selector--pdp` exists). For every
     other product the bare {% form %} would do a native POST that redirects
     to /cart — so we ajax-add here and reuse the theme's drawer openers,
     yielding to the sibling handler when it owns the submit. ------------- */
  function isSiblingPdp() {
    return !!document.querySelector(".m-sibling-selector--pdp");
  }

  function openCartDrawer() {
    var cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.open === "function") {
      cartDrawer.open();
      return;
    }
    if (typeof window.refreshCartDrawer === "function") window.refreshCartDrawer();
    if (typeof window.openCartDrawer === "function") window.openCartDrawer();
    document.body.classList.add("cart-open");
    var opener = document.querySelector("#cart-icon-bubble, .js-drawer-open-right, .cart-link, .cart-icon");
    if (opener) opener.click();
  }

  function getAjaxCartAddUrl() {
    var url = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : "/cart/add";
    return /\.js(\?|$)/.test(url) ? url : url + ".js";
  }

  function getCartSectionIds(cartDrawer) {
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
      return cartDrawer.getSectionsToRender().map(function (section) {
        return section.id || section.section;
      }).filter(Boolean);
    }
    return ["cart-drawer", "cart-icon-bubble"];
  }

  function parseCartJson(response) {
    return response.text().then(function (text) {
      var payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch (e) {
        payload = null;
      }

      if (!response.ok || !payload || payload.status || payload.errors) {
        throw new Error(
          (payload && (payload.description || payload.message)) ||
          "Could not add the selected products. Please try again."
        );
      }

      return payload;
    });
  }

  function applyCartSectionHtml(sections) {
    if (!sections) return false;
    var applied = false;
    var drawerHtml = sections["cart-drawer"];
    var drawerTarget = document.getElementById("CartDrawer");
    if (drawerHtml && drawerTarget) {
      var source = new DOMParser()
        .parseFromString(drawerHtml, "text/html")
        .querySelector("#CartDrawer");
      if (source) {
        drawerTarget.innerHTML = source.innerHTML;
        applied = true;
      }
    }

    var bubble = document.getElementById("cart-icon-bubble");
    if (bubble && sections["cart-icon-bubble"]) {
      bubble.innerHTML = sections["cart-icon-bubble"];
      applied = true;
    }

    return applied;
  }

  function refreshCartSections(cartDrawer) {
    var url =
      window.location.pathname +
      "?sections=cart-drawer,cart-icon-bubble&_=" +
      Date.now();

    return fetch(url, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" }
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Cart refresh failed.");
        return response.json();
      })
      .then(function (sections) {
        var payload = { item_count: 1, sections: sections };
        if (cartDrawer && typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(payload);
        } else {
          applyCartSectionHtml(sections);
        }
        return payload;
      });
  }

  function renderCartPayload(payload, cartDrawer) {
    if (cartDrawer && payload && payload.sections && typeof cartDrawer.renderContents === "function") {
      if (typeof cartDrawer.setActiveElement === "function") {
        cartDrawer.setActiveElement(document.activeElement);
      }
      cartDrawer.renderContents(payload);
      return Promise.resolve(payload);
    }

    if (payload && payload.sections && applyCartSectionHtml(payload.sections)) {
      return Promise.resolve(payload);
    }

    return refreshCartSections(cartDrawer).then(function (freshPayload) {
      if (cartDrawer && typeof cartDrawer.open === "function") {
        openCartDrawer();
      }
      return freshPayload;
    });
  }

  function setPdpAtcLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle("is-loading", !!isLoading);
    button.toggleAttribute("aria-busy", !!isLoading);

    var spinner = button.querySelector(".pdp-v2__atc-spinner");
    if (isLoading) {
      if (!spinner) {
        spinner = document.createElement("span");
        spinner.className = "pdp-v2__atc-spinner";
        spinner.setAttribute("aria-hidden", "true");
        button.appendChild(spinner);
      }
      return;
    }

    if (spinner) spinner.remove();
  }

  function setPdpFormLoading(form, isLoading) {
    if (!form) return;
    form.querySelectorAll(".pdp-v2__atc").forEach(function (button) {
      setPdpAtcLoading(button, isLoading);
    });
    if (!isLoading && form.__pdpAtcLoadingTimer) {
      window.clearTimeout(form.__pdpAtcLoadingTimer);
      form.__pdpAtcLoadingTimer = null;
    }
  }

  function initAtcLoading(root) {
    var form = root.querySelector(".new-pdp-atc-form");
    if (!form || form.__pdpAtcLoadingBound) return;
    form.__pdpAtcLoadingBound = true;

    form.addEventListener("submit", function () {
      setPdpFormLoading(form, true);
      if (form.__pdpAtcLoadingTimer) window.clearTimeout(form.__pdpAtcLoadingTimer);
      form.__pdpAtcLoadingTimer = window.setTimeout(function () {
        setPdpFormLoading(form, false);
      }, 6000);
    }, true);

    document.addEventListener("theme:cart:added", function () {
      setPdpFormLoading(form, false);
    });
    document.addEventListener("cart:updated", function () {
      setPdpFormLoading(form, false);
    });
  }

  function initAjaxAtc(root) {
    var form = root.querySelector(".new-pdp-atc-form");
    if (!form || form.__pdpAtcBound) return;
    form.__pdpAtcBound = true;

    form.addEventListener("submit", function (e) {
      if (form.dataset.cartDrawerSubmitting === "true") return;
      if (isSiblingPdp()) return; // m-custom.js owns sibling submits
      e.preventDefault();

      var buttons = form.querySelectorAll('button[type="submit"]');
      buttons.forEach(function (b) { b.disabled = true; setPdpAtcLoading(b, true); });

      fetch(getAjaxCartAddUrl(), {
        method: "POST",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: new FormData(form)
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          buttons.forEach(function (b) { b.disabled = false; setPdpAtcLoading(b, false); });
          openCartDrawer();
        })
        .catch(function () {
          buttons.forEach(function (b) { b.disabled = false; setPdpAtcLoading(b, false); });
          form.submit(); // last-resort native add so the user is never stuck
        });
    });
  }

  /* --- Tabs (Phase 2 sections can opt in via .pdp-v2-tabs) -------------- */
  function initTabs(scope) {
    scope.querySelectorAll(".pdp-v2-tabs").forEach(function (tabs) {
      var btns = Array.prototype.slice.call(tabs.querySelectorAll("[role=tab]"));
      var panels = Array.prototype.slice.call(tabs.querySelectorAll("[role=tabpanel]"));
      if (!btns.length) return;

      function activate(i) {
        btns.forEach(function (b, j) {
          var on = i === j;
          b.setAttribute("aria-selected", on ? "true" : "false");
          b.tabIndex = on ? 0 : -1;
          b.classList.toggle("is-active", on);
        });
        panels.forEach(function (p, j) {
          p.hidden = i !== j;
          if (i === j) p.classList.add("reveal");
        });
      }

      btns.forEach(function (btn, i) {
        btn.addEventListener("click", function () { activate(i); });
        btn.addEventListener("keydown", function (e) {
          var idx = null;
          if (e.key === "ArrowRight") idx = (i + 1) % btns.length;
          if (e.key === "ArrowLeft") idx = (i - 1 + btns.length) % btns.length;
          if (idx != null) { btns[idx].focus(); activate(idx); e.preventDefault(); }
        });
      });
      activate(0);
    });
  }

  /* --- FAQ accordion ----------------------------------------------------- */
  function initFaq(scope) {
    scope.querySelectorAll(".pdp-v2-faq__item").forEach(function (item) {
      var btn = item.querySelector(".pdp-v2-faq__q");
      var answer = item.querySelector(".pdp-v2-faq__a");
      if (!btn || !answer) return;
      btn.setAttribute("aria-expanded", "false");
      answer.hidden = true;
      btn.addEventListener("click", function () {
        var open = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", open ? "false" : "true");
        item.classList.toggle("is-open", !open);
        answer.hidden = open;
        if (!open) answer.classList.add("reveal");
      });
    });
  }

  /* --- "Complete the ritual" bundle total -------------------------------- */
  function initBundle(scope) {
    scope.querySelectorAll(".pdp-v2-bundle").forEach(function (bundle) {
      var checks = Array.prototype.slice.call(
        bundle.querySelectorAll(".pdp-v2-bundle__check")
      );
      var totalEl = bundle.querySelector(".pdp-v2-bundle__total-value");
      if (!checks.length || !totalEl) return;
      var mrpEl = bundle.querySelector(".pdp-v2-bundle__mrp");
      var saveEl = bundle.querySelector(".pdp-v2-bundle__save");
      var currentCheck = bundle.querySelector('[data-bundle-current="true"]');
      var symbol = totalEl.getAttribute("data-currency-symbol") || "";

      function fmt(cents) {
        return symbol + (cents / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });
      }

      function selectedPdpVariantInput() {
        var form = document.querySelector(".new-pdp-atc-form");
        if (!form) return null;
        return (
          form.querySelector('input[name="id"]:checked') ||
          form.querySelector('select[name="id"]') ||
          form.querySelector('.m-variant-input[name="id"], .m_product_varaint_input[name="id"]') ||
          form.querySelector('input[name="id"]')
        );
      }

      function centsFrom(node, attr, fallback) {
        if (!node) return fallback || 0;
        var parsed = parseInt(node.getAttribute(attr), 10);
        return isNaN(parsed) ? (fallback || 0) : parsed;
      }

      function syncCurrentProductRow() {
        if (!currentCheck) return;
        var selected = selectedPdpVariantInput();
        if (!selected || !selected.value) return;

        var previousPrice = centsFrom(currentCheck, "data-price-cents", 0);
        var priceCents = centsFrom(selected, "data-price-cents", previousPrice);
        var compareCents = centsFrom(selected, "data-compare-cents", priceCents);

        currentCheck.checked = true;
        currentCheck.setAttribute("data-variant-id", selected.value);
        currentCheck.setAttribute("data-price-cents", priceCents);
        currentCheck.setAttribute("data-compare-cents", compareCents > priceCents ? compareCents : priceCents);

        var row = currentCheck.closest(".pdp-v2-bundle__row");
        if (!row) return;
        var nowEl = row.querySelector(".pdp-v2-bundle__price-now");
        if (nowEl) nowEl.textContent = fmt(priceCents);

        var priceWrap = row.querySelector(".pdp-v2-bundle__price");
        var wasEl = row.querySelector(".pdp-v2-bundle__price-was");
        if (compareCents > priceCents) {
          if (!wasEl && priceWrap) {
            wasEl = document.createElement("span");
            wasEl.className = "pdp-v2-bundle__price-was";
            priceWrap.appendChild(wasEl);
          }
          if (wasEl) {
            wasEl.style.display = "";
            wasEl.textContent = fmt(compareCents);
          }
        } else if (wasEl) {
          wasEl.style.display = "none";
          wasEl.textContent = "";
        }
      }

      function recalc() {
        syncCurrentProductRow();
        var total = 0, compare = 0;
        checks.forEach(function (c) {
          if (!c.checked) return;
          var p = parseInt(c.getAttribute("data-price-cents"), 10) || 0;
          var cmp = parseInt(c.getAttribute("data-compare-cents"), 10) || p;
          total += p;
          compare += cmp;
        });
        totalEl.textContent = fmt(total);
        var saved = compare - total;
        // Show the struck MRP + "You save" only when the bundle actually saves money.
        if (mrpEl) { mrpEl.hidden = saved <= 0; if (saved > 0) mrpEl.textContent = fmt(compare); }
        if (saveEl) { saveEl.hidden = saved <= 0; if (saved > 0) saveEl.textContent = "You save " + fmt(saved); }
      }
      checks.forEach(function (c) { c.addEventListener("change", recalc); });
      document.addEventListener("change", function (event) {
        var target = event.target;
        if (!target || !target.closest || !target.closest(".new-pdp-atc-form")) return;
        if (!target.matches('input[name="id"], select[name="id"], .m-variant-input, .m_product_varaint_input')) return;
        recalc();
      });
      recalc();

      function selectedBundleItems() {
        var quantities = {};
        checks.forEach(function (c) {
          var variantId = parseInt(c.getAttribute("data-variant-id"), 10);
          if (!c.checked || !variantId) return;
          quantities[variantId] = (quantities[variantId] || 0) + 1;
        });

        return Object.keys(quantities).map(function (id) {
          return { id: parseInt(id, 10), quantity: quantities[id] };
        });
      }

      var addBtn = bundle.querySelector(".pdp-v2-bundle__add");
      if (addBtn) {
        addBtn.addEventListener("click", function () {
          syncCurrentProductRow();
          var items = selectedBundleItems();
          if (!items.length) return;
          var cartDrawer = document.querySelector("cart-drawer");

          addBtn.disabled = true;
          addBtn.setAttribute("aria-busy", "true");

          fetch(getAjaxCartAddUrl(), {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest"
            },
            body: JSON.stringify({
              items: items,
              sections: getCartSectionIds(cartDrawer),
              sections_url: window.location.pathname
            })
          })
            .then(parseCartJson)
            .then(function (payload) {
              return renderCartPayload(payload, cartDrawer);
            })
            .then(function (payload) {
              document.dispatchEvent(new CustomEvent("theme:cart:added", { detail: payload }));
              openCartDrawer();
            })
            .catch(function (error) {
              window.alert(error.message || "Could not add the selected products. Please try again.");
            })
            .then(function () {
              addBtn.disabled = false;
              addBtn.removeAttribute("aria-busy");
            });
        });
      }
    });
  }

  /* --- Sticky add bar: permanent on product pages. ----------------------- */
  function initStickyBar(root) {
    var bar = document.getElementById("stickycart") || root.querySelector(".m-sticky-atc");
    if (bar) bar.classList.add("m-sticky-atc--visible");
  }

  ready(function () {
    var roots = document.querySelectorAll(".pdp-v2");
    roots.forEach(function (root) {
      initGallery(root);
      initSizeCards(root);
      initSizeScroller(root);
      initSiblingScrollerRefresh(root);
      initQty(root);
      initReadMore(root);
      initCouponCopy(root);
      initPincodeState(root);
      initAtcLoading(root);
      initAjaxAtc(root);
      initStickyBar(root);
    });
    // Section-level helpers can live outside the main .pdp-v2 wrapper.
    initTabs(document);
    initAPlusContent(document);
    initFaq(document);
    initBundle(document);
  });
})();
